import React from 'react';
import { CalendarOutlined } from '@ant-design/icons';
import { cn } from '@/lib/utils';

export const PIPELINE_INPUT_CLASS =
  '!border-0 !shadow-none !bg-transparent hover:!bg-gray-50 dark:hover:!bg-zinc-800/50 !px-1 !rounded';

/** Ícone discreto à direita dos campos de data no estilo Kommo */
export const PIPELINE_DATE_SUFFIX = (
  <CalendarOutlined className="text-[#b0b8c4] dark:text-gray-500 text-[13px]" />
);

interface PipelineFieldRowProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  error?: string;
}

export function PipelineFieldRow({
  label,
  required,
  children,
  error,
}: PipelineFieldRowProps) {
  return (
    <div className="grid grid-cols-[132px_1fr] gap-2 items-start px-3 py-2 border-b border-[#eceef2] dark:border-zinc-800 min-h-[40px]">
      <span className="text-[13px] text-[#8f9aab] dark:text-gray-500 leading-8">
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </span>
      <div className="min-w-0">
        {children}
        {error && <p className="text-[11px] text-red-500 mt-0.5">{error}</p>}
      </div>
    </div>
  );
}

/** Linha somente leitura — label à esquerda, valor à direita */
export function PipelineInfoRow({
  label,
  value,
  children,
  className,
}: {
  label: string;
  value?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'grid grid-cols-[132px_1fr] gap-2 items-start px-3 py-2 border-b border-[#eceef2] dark:border-zinc-800 min-h-[40px]',
        className
      )}
    >
      <span className="text-[13px] text-[#8f9aab] dark:text-gray-500 leading-8">{label}</span>
      <div className="min-w-0 text-[13px] text-gray-900 dark:text-gray-100 leading-8 break-words">
        {children ?? value ?? '—'}
      </div>
    </div>
  );
}

/** Bloco contínuo estilo Kommo para agrupar linhas */
export function PipelineGroup({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        '-mx-1 rounded-md border border-[#eceef2] dark:border-zinc-800 overflow-hidden [&>*:last-child]:border-b-0',
        className
      )}
    >
      {children}
    </div>
  );
}

export function PipelineSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] text-[#8f9aab] dark:text-gray-500 uppercase tracking-wide px-3 pt-3 pb-1 font-medium">
      {children}
    </p>
  );
}

/** Link/ação discreta estilo Kommo */
export function PipelineActionLink({
  onClick,
  children,
  className,
  as: Tag = 'button',
}: {
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  as?: 'button' | 'label';
}) {
  const classes = cn(
    'flex items-center gap-1.5 px-3 py-2 text-[13px] text-[#FF3737] hover:bg-gray-50 dark:hover:bg-zinc-800/50 rounded-md transition-colors w-full text-left cursor-pointer',
    className
  );
  if (Tag === 'label') {
    return <label className={classes}>{children}</label>;
  }
  return (
    <button type="button" onClick={onClick} className={classes}>
      {children}
    </button>
  );
}

export function PipelineEmbeddedEmpty({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[13px] text-[#8f9aab] dark:text-gray-500 text-center py-8 px-3">
      {children}
    </p>
  );
}

/** Wrapper para tabelas Ant Design no painel embedded */
export function PipelineTableWrap({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('-mx-1 rounded-md border border-[#eceef2] dark:border-zinc-800 overflow-hidden pipeline-embedded-table-wrap', className)}>
      {children}
    </div>
  );
}
